import { useRef, useEffect } from "preact/hooks"
import { marked } from "marked"
import { ToolCallCard } from "./ToolCallCard"
import { ConfirmationCard } from "./ConfirmationCard"
import { ThinkingBlock, ThinkingBlockStyles } from "./ThinkingBlock"
import { Wand2, User, FileText } from "lucide-react"
import { ConfirmationAction } from "@/api/wizard"
import type { DeductionKind } from "@/api/deduction"
import { format_transaction_amount } from "@/utils"
import { DisplayMessage, ToolCallDisplay } from "./page"

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
})

interface MessageListProps {
  messages: DisplayMessage[]
  isStreaming: boolean
  thinkingMessage: string | null
  streamingThinking: string
  isThinkingPhase: boolean
  streamingText: string
  activeToolCalls: ToolCallDisplay[]
  conversationId: string | null
  className?: string
}

export function MessageList({
  messages,
  isStreaming,
  thinkingMessage,
  streamingThinking,
  isThinkingPhase,
  streamingText,
  activeToolCalls,
  conversationId,
  className,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, thinkingMessage, streamingThinking, streamingText, activeToolCalls])

  return (
    <>
      <WizardMarkdownStyles />
      <ThinkingBlockStyles />
      <div
        ref={scrollRef}
        class={`flex-1 overflow-y-auto px-4 py-6 space-y-6 ${className || ""}`}
      >
        {messages.map((message: DisplayMessage) => (
          <MessageBubble
            key={message.id}
            message={message}
            conversationId={conversationId}
          />
        ))}

        {/* Streaming assistant response (in-progress) */}
        {isStreaming && (streamingThinking || streamingText || activeToolCalls.length > 0 || thinkingMessage) && (
          <div class="flex gap-3">
            <AgentAvatar />
            <div class="flex-1 max-w-[80%] space-y-2">
              {/* Thinking block while streaming */}
              {streamingThinking && (
                <ThinkingBlock
                  content={streamingThinking}
                  isStreaming={isThinkingPhase}
                  defaultExpanded
                />
              )}

              {/* Active tool calls */}
              {activeToolCalls.length > 0 && (
                <div class="space-y-2">
                  {activeToolCalls.map((tc: ToolCallDisplay, i: number) => (
                    <ToolCallCard key={`${tc.toolName}-${i}`} toolCall={tc} />
                  ))}
                </div>
              )}

              {/* Streaming text response */}
              {streamingText ? (
                <StreamingAssistantContent content={streamingText} />
              ) : thinkingMessage && !streamingThinking ? (
                /* Status message (pre-thinking, between iterations) */
                <div class="flex items-center gap-2 text-sm text-gray-500">
                  <ThinkingDots />
                  <span>{thinkingMessage}</span>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

interface MessageBubbleProps {
  message: DisplayMessage
  conversationId: string | null
}

function MessageBubble({ message, conversationId }: MessageBubbleProps) {
  const isUser = message.role === "user"

  return (
    <div class={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      {isUser ? <UserAvatar /> : <AgentAvatar />}
      <div class={`flex-1 max-w-[80%] ${isUser ? "flex flex-col items-end" : ""}`}>
        {/* Thinking block for saved messages */}
        {!isUser && message.thinking && (
          <ThinkingBlock
            content={message.thinking}
            isStreaming={false}
            defaultExpanded={false}
          />
        )}

        {message.toolCalls && message.toolCalls.length > 0 && (
          <div class="space-y-2 mb-3 w-full">
            {message.toolCalls.map((tc: ToolCallDisplay, i: number) => (
              <ToolCallCard key={`${tc.toolName}-${i}`} toolCall={tc} />
            ))}
          </div>
        )}

        {message.confirmationAction && conversationId && (
          <div class="mb-3 w-full">
            <ConfirmationCard
              action={message.confirmationAction}
              conversationId={conversationId}
            />
          </div>
        )}

        {/* Uploaded files indicator on user messages */}
        {isUser && message.uploadedFiles && message.uploadedFiles.length > 0 && (
          <UploadedFilesBadge fileNames={message.uploadedFiles} />
        )}

        {/* Attached deduction indicator on user messages */}
        {isUser && message.attachedDeduction && (
          <AttachedDeductionBadge deduction={message.attachedDeduction} />
        )}

        {message.content && (
          isUser ? (
            <div class="rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap bg-primary text-white">
              {message.content}
            </div>
          ) : (
            <AssistantContent content={message.content} />
          )
        )}
      </div>
    </div>
  )
}

function AssistantContent({ content }: { content: string }) {
  const html = marked.parse(content) as string
  return (
    <div
      class="wizard-markdown text-sm text-gray-800"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

/** Streaming text with a blinking cursor at the end */
function StreamingAssistantContent({ content }: { content: string }) {
  const html = marked.parse(content) as string
  return (
    <div class="relative">
      <div
        class="wizard-markdown text-sm text-gray-800"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <span class="inline-block w-0.5 h-4 bg-gray-400 animate-blink align-text-bottom" />
    </div>
  )
}

function AgentAvatar() {
  return (
    <div class="shrink-0 w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
      <Wand2 className="w-4 h-4" style={{ color: "white" }} />
    </div>
  )
}

function UserAvatar() {
  return (
    <div class="shrink-0 w-8 h-8 rounded-lg bg-gray-300 flex items-center justify-center">
      <User className="w-4 h-4 text-gray-600" />
    </div>
  )
}

/** Badge shown on user messages that have an attached deduction */
function AttachedDeductionBadge({
  deduction,
}: {
  deduction: { id: string; invoice_number?: string; invoice_amount?: number; kind?: DeductionKind; source?: string }
}) {
  const label = deduction.invoice_number || deduction.id.slice(0, 8)
  const amount =
    deduction.invoice_amount != null
      ? format_transaction_amount(deduction.kind ?? "deduction", deduction.invoice_amount)
      : null

  return (
    <a
      href={`/deduction/${deduction.id}`}
      class="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-lg bg-primary/5 border border-primary/20 text-xs hover:bg-primary/10 transition-colors no-underline"
    >
      <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
      <span class="font-medium text-primary">{label}</span>
      {amount && <span class="text-primary/60">{amount}</span>}
      {deduction.source && <span class="text-primary/60 uppercase">{deduction.source}</span>}
    </a>
  )
}

/** Badge(s) shown on user messages that have uploaded files */
function UploadedFilesBadge({ fileNames }: { fileNames: string[] }) {
  return (
    <div class="flex flex-wrap gap-1 mb-2 justify-end">
      {fileNames.map((name: string, i: number) => (
        <span
          key={`${name}-${i}`}
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-xs text-gray-600"
        >
          <FileText className="w-3 h-3 text-gray-400 shrink-0" />
          <span class="truncate max-w-[150px]">{name}</span>
        </span>
      ))}
    </div>
  )
}

function ThinkingDots() {
  return (
    <div class="flex gap-1">
      <div class="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
      <div class="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
      <div class="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  )
}

/** Scoped styles for markdown rendered inside wizard assistant messages */
function WizardMarkdownStyles() {
  return (
    <style>{`
      .wizard-markdown {
        line-height: 1.7;
      }
      .wizard-markdown > *:first-child {
        margin-top: 0;
      }
      .wizard-markdown > *:last-child {
        margin-bottom: 0;
      }

      /* Headings */
      .wizard-markdown h1,
      .wizard-markdown h2,
      .wizard-markdown h3,
      .wizard-markdown h4 {
        font-weight: 600;
        margin-top: 1.25em;
        margin-bottom: 0.5em;
        color: #1f2937;
      }
      .wizard-markdown h1 { font-size: 1.4em; }
      .wizard-markdown h2 { font-size: 1.2em; }
      .wizard-markdown h3 { font-size: 1.05em; }

      /* Paragraphs */
      .wizard-markdown p {
        margin-bottom: 0.75em;
      }

      /* Lists */
      .wizard-markdown ul {
        list-style-type: disc;
        margin-left: 1.5em;
        margin-bottom: 0.75em;
      }
      .wizard-markdown ol {
        list-style-type: decimal;
        margin-left: 1.5em;
        margin-bottom: 0.75em;
      }
      .wizard-markdown li {
        margin-bottom: 0.25em;
      }
      .wizard-markdown li > p {
        margin-bottom: 0.25em;
      }

      /* Bold & italic */
      .wizard-markdown strong {
        font-weight: 600;
        color: #111827;
      }
      .wizard-markdown em {
        font-style: italic;
      }

      /* Inline code */
      .wizard-markdown code {
        background-color: #f3f4f6;
        padding: 0.15em 0.35em;
        border-radius: 0.25em;
        font-size: 0.88em;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      }

      /* Code blocks */
      .wizard-markdown pre {
        background-color: #1f2937;
        color: #e5e7eb;
        padding: 0.875em 1em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.75em 0;
        font-size: 0.85em;
      }
      .wizard-markdown pre code {
        background-color: transparent;
        padding: 0;
        color: inherit;
      }

      /* Tables */
      .wizard-markdown table {
        border-collapse: collapse;
        width: 100%;
        margin: 0.75em 0;
        font-size: 0.9em;
        overflow-x: auto;
        display: block;
      }
      .wizard-markdown thead {
        background-color: #f9fafb;
      }
      .wizard-markdown th {
        font-weight: 600;
        color: #374151;
        padding: 0.5em 0.75em;
        border-bottom: 2px solid #e5e7eb;
        text-align: left;
        white-space: nowrap;
      }
      .wizard-markdown td {
        padding: 0.5em 0.75em;
        border-bottom: 1px solid #f3f4f6;
        color: #4b5563;
      }
      .wizard-markdown tbody tr:hover {
        background-color: #f9fafb;
      }

      /* Blockquotes */
      .wizard-markdown blockquote {
        border-left: 3px solid #818cf8;
        padding-left: 1em;
        margin: 0.75em 0;
        color: #6b7280;
        font-style: italic;
      }

      /* Horizontal rules */
      .wizard-markdown hr {
        border: none;
        border-top: 1px solid #e5e7eb;
        margin: 1em 0;
      }

      /* Links */
      .wizard-markdown a {
        color: #6366f1;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      .wizard-markdown a:hover {
        color: #4f46e5;
      }
    `}</style>
  )
}
